import { useState, useMemo, useCallback, useEffect } from 'react';
import * as S from './styles';
import { useClientHierarchy } from '@/hooks';
import { useUserContext } from '@/hooks';

type Props = {
  asView?: boolean;

  onClose?: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
};

const CoordinatorList = ({ asView = false, onClose, onLoadingChange }: Props) => {
  const { hierarchy, handleRCALogin, setViewType } = useClientHierarchy();
  const { customer } = useUserContext();

  const [filter, setFilter] = useState('');
  const [selectedRCA, setSelectedRCA] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setViewType(!!asView);
  }, [asView, setViewType]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const filteredHierarchy = useMemo(() => {
  if (!hierarchy) return hierarchy;

  const lowerCaseFilter = (filter || '').toLowerCase();

  const base = !filter
    ? [...hierarchy]
    : hierarchy.filter((item: any) => {
        const nome = String(item?.nome_usuario || '').toLowerCase();
        const codLoja = String(item?.cod_loja || '');
        const codPessoa = String(item?.cod_pessoa || '');
        const codSegmento = String(item?.cod_segmento || '');

        return (
          nome.includes(lowerCaseFilter) ||
          codLoja.includes(filter) ||
          codPessoa.includes(filter) ||
          codSegmento.includes(filter)
        );
      });

  return base.sort((a: any, b: any) => {
    const an = String(a?.nome_usuario || '').trim().toLowerCase();
    const bn = String(b?.nome_usuario || '').trim().toLowerCase();
    return an.localeCompare(bn, 'pt-BR', { sensitivity: 'base' });
  });
}, [filter, hierarchy]);

  const isSelected = useCallback(
    (id: number) => selectedRCA?.cod_pessoa === id,
    [selectedRCA]
  );

  const handleStartLogin = useCallback(async () => {
    if (!selectedRCA) return;

    setIsLoading(true);

    try {
      await handleRCALogin(selectedRCA);
      await new Promise((resolve) => setTimeout(resolve, 300));
      onClose?.();
    } catch (error) {
      console.error('Erro ao entrar como RCA:', error);
    } finally {
      setIsLoading(false);
    }
  }, [handleRCALogin, onClose, selectedRCA]);

  return (
    <>
    {!asView && (
      <>
      <S.ModalTitle>
        Olá, {customer?.firstname} {customer?.lastname || ''}
      </S.ModalTitle>
      </>
    )}
      <S.InputContent>
        <S.SearchIcon $isgray size={24} />
        <S.InputModal
          placeholder="Busque por nome ou código do RCA"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </S.InputContent>

      <S.ModalContent>
        <S.CoordinatorInfo>
          <p>Todos os RCAs de: </p>
          <strong>{customer?.firstname}</strong>
        </S.CoordinatorInfo>

        <S.ContentListCompany>
          {filteredHierarchy?.map((item: any) => (
            <S.RCACard key={item.cod_pessoa} onClick={() => setSelectedRCA(item)}>
              <S.CheboxContent $isdisabled={false}>
                {isSelected(item?.cod_pessoa) ? <S.CheckboxCircle /> : <S.CheckboxBlankCircle />}
              </S.CheboxContent>

              <S.RCABottomInfo>
                <S.RCAName>{item?.nome_usuario}</S.RCAName>
                <S.RCADescription>
                  {`Cod Loja - ${item?.cod_loja} | Cod RCA - ${item?.cod_pessoa}`}
                </S.RCADescription>
              </S.RCABottomInfo>
            </S.RCACard>
          ))}
        </S.ContentListCompany>
      </S.ModalContent>

      <S.BottomContent>
        <S.ButtonAccess onClick={handleStartLogin} disabled={!selectedRCA || isLoading}>
          {isLoading ? 'Entrando...' : 'Entrar como Representante Comercial'}
        </S.ButtonAccess>
      </S.BottomContent>
    </>
  );
};

export default CoordinatorList;
